import { Dock, PageScroll } from "@Components/Page";
import { styled } from "@mui/material/styles";
import { Banner, Button, Card, Field, GroupedList, ListRow, Meta, PageTitle, PushedBar, SendIcon, Tag, TextArea, TextInput, Toggle } from "@UI";
import React from "react";
import { useTranslation } from "react-i18next";
const PromptText = styled(Card)({
    padding: "var(--j-sp-3)",
    border: "1px solid var(--j-line)",
    borderRadius: "var(--j-r-md)",
    background: "var(--j-panel)",
    lineHeight: "var(--j-lh-lg)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
});
const Heading = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "var(--j-sp-2)"
});
const Setting = styled(GroupedList)({
    margin: 0,
    borderTop: "1px solid var(--j-line)",
    borderBottom: "1px solid var(--j-line)",
    "&&& > .jc-row": {
        paddingLeft: 0,
        paddingRight: 0
    }
});
export function PromptEdit({ prompt, setPrompt, onSave, onStop, onUse, isEditable, isSaving, hasSaveError }) {
    const { t, i18n } = useTranslation();
    const isSaveable = !!prompt.prompt && !!prompt.title;
    const isPrivate = prompt.type === "private";
    const byline = prompt.userName ? t("prompts.byName", {
        name: prompt.userName
    }) : null;
    if (!isEditable) {
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PushedBar, {
            onBack: onStop,
            backLabel: t("prompts.multiple")
        }), /*#__PURE__*/ React.createElement(PageScroll, null, /*#__PURE__*/ React.createElement(Heading, null, /*#__PURE__*/ React.createElement(Tag, null, t("prompts.juristicTag")), /*#__PURE__*/ React.createElement(PageTitle, {
            title: prompt.title,
            lede: byline
        })), /*#__PURE__*/ React.createElement(PromptText, null, prompt.prompt)), prompt.prompt && /*#__PURE__*/ React.createElement(Dock, null, /*#__PURE__*/ React.createElement(Button, {
            size: "lg",
            startIcon: /*#__PURE__*/ React.createElement(SendIcon, {
                size: 14
            }),
            onClick: ()=>onUse(prompt.prompt)
        }, t("prompts.use")), /*#__PURE__*/ React.createElement(Meta, {
            style: {
                textAlign: "center"
            }
        }, t("prompts.useHint"))));
    }
    const edited = prompt.updatedAt ? new Date(prompt.updatedAt).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short"
    }) : null;
    const save = ()=>onSave(prompt);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PushedBar, {
        onBack: onStop,
        backLabel: t("prompts.multiple"),
        title: t(prompt.id ? "prompts.edit" : "prompts.newPrompt")
    }), /*#__PURE__*/ React.createElement(PageScroll, {
        style: {
            gap: "var(--j-sp-4)"
        }
    }, /*#__PURE__*/ React.createElement(Field, {
        label: t("title")
    }, /*#__PURE__*/ React.createElement(TextInput, {
        value: prompt.title ?? "",
        onChange: (e)=>setPrompt({
                ...prompt,
                title: e.target.value
            })
    })), /*#__PURE__*/ React.createElement(Field, {
        label: t("prompts.single")
    }, /*#__PURE__*/ React.createElement(TextArea, {
        minRows: 12,
        maxRows: 16,
        value: prompt.prompt ?? "",
        onChange: (e)=>setPrompt({
                ...prompt,
                prompt: e.target.value
            })
    })), /*#__PURE__*/ React.createElement(Setting, null, /*#__PURE__*/ React.createElement(ListRow, {
        title: t("private"),
        titleWeight: 500,
        description: t(isPrivate ? "prompts.privateHint" : "prompts.teamHint"),
        trailing: /*#__PURE__*/ React.createElement(Toggle, {
            "aria-label": t("private"),
            checked: isPrivate,
            onChange: (checked)=>setPrompt({
                    ...prompt,
                    type: checked ? "private" : "organization"
                })
        })
    })), byline && /*#__PURE__*/ React.createElement(Meta, {
        style: {
            textAlign: "center"
        }
    }, byline, edited && ` · ${t("prompts.edited", {
        date: edited
    })}`)), /*#__PURE__*/ React.createElement(Dock, null, hasSaveError && /*#__PURE__*/ React.createElement(Banner, {
        tone: "error"
    }, t("prompts.saveFailed")), /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        disabled: !isSaveable,
        loading: isSaving,
        onClick: save
    }, t("prompts.save"))));
}
